import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from '@radix-ui/react-slot'
import * as RD from '@radix-ui/react-dialog'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { MoreVertical, X } from 'lucide-react'
import { cn } from '../lib/utils'

/* The primitives. shadcn-shaped -- copied into the repo rather than imported,
   so they can be changed without fighting a dependency. */

// --- button -----------------------------------------------------------------

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[--radius-control] ' +
    'text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-45 ' +
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:translate-y-px",
  {
    variants: {
      variant: {
        // Amber is reserved for the one action a screen exists for. If two
        // buttons on a screen are amber, neither reads as the primary one.
        primary:
          'bg-brand text-brand-ink hover:bg-brand-hot shadow-[0_1px_0_oklch(1_0_0/0.25)_inset,0_2px_10px_oklch(0.78_0.155_72/0.25)]',
        secondary:
          'bg-surface-2 text-ink border border-line hover:bg-surface-3 hover:border-line-strong',
        ghost: 'text-ink-dim hover:text-ink hover:bg-surface-2',
        danger: 'bg-bad/15 text-bad border border-bad/30 hover:bg-bad/25',
        link: 'text-brand hover:text-brand-hot underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-9.5 px-4',
        lg: 'h-11 px-5 text-[15px]',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  },
)
Button.displayName = 'Button'

// --- surfaces ---------------------------------------------------------------

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('panel p-5', className)} {...props} />
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-base font-semibold', className)} {...props} />
}

export function Dialog({
  open, onOpenChange, title, children, closeDisabled = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  children: React.ReactNode
  closeDisabled?: boolean
}) {
  return (
    <RD.Root open={open} onOpenChange={open => {
      if (!open && closeDisabled) return
      onOpenChange(open)
    }}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-50 bg-black/65 backdrop-blur-[2px]" />
        <RD.Content
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(44rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[--radius-card] border border-line bg-surface-1 p-5 shadow-[0_24px_80px_oklch(0_0_0/0.6)] focus:outline-none"
        >
          <div className="mb-5 flex items-center justify-between gap-4">
            <RD.Title className="text-base font-semibold text-ink">{title}</RD.Title>
            <RD.Close asChild disabled={closeDisabled}>
              <Button variant="ghost" size="icon" aria-label="Close" disabled={closeDisabled}>
                <X />
              </Button>
            </RD.Close>
          </div>
          {children}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  )
}

export function SectionLabel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint', className)}
      {...props}
    />
  )
}

// --- form controls ----------------------------------------------------------

const fieldBase =
  'w-full rounded-[--radius-control] bg-surface-2 border border-line px-3 text-sm text-ink ' +
  'placeholder:text-ink-faint transition-colors hover:border-line-strong ' +
  'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input ref={ref} className={cn(fieldBase, 'h-9.5', className)} {...props} />
  ),
)
Input.displayName = 'Input'

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea ref={ref} className={cn(fieldBase, 'py-2.5 leading-relaxed resize-y', className)} {...props} />
))
Textarea.displayName = 'Textarea'

/** A labelled control with optional help and error text.
 *
 *  Help sits UNDER the control rather than above it: read in order, the label
 *  says what this is, the control is what you act on, and the help explains
 *  the consequence. Guidance above the input pushes the input itself down the
 *  page and gets skipped anyway. */
export function Field({
  label, help, error, children, className, htmlFor,
}: {
  label?: string
  help?: React.ReactNode
  error?: string | null
  children: React.ReactNode
  className?: string
  htmlFor?: string
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="block text-[13px] font-medium text-ink-dim">
          {label}
        </label>
      )}
      {children}
      {help && !error && <p className="text-xs leading-relaxed text-ink-faint">{help}</p>}
      {error && <p className="text-xs text-bad">{error}</p>}
    </div>
  )
}

// --- status -----------------------------------------------------------------

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize',
  {
    variants: {
      tone: {
        neutral: 'border-line bg-surface-2 text-ink-dim',
        good: 'border-good/35 bg-good/12 text-good',
        warn: 'border-warn/35 bg-warn/12 text-warn',
        bad: 'border-bad/35 bg-bad/12 text-bad',
        brand: 'border-brand/40 bg-brand/12 text-brand',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export function Badge({
  className, tone, dot, ...props
}: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants> & { dot?: boolean }) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {dot && <span className="pulse-dot size-1.5 rounded-full bg-current" />}
      {props.children}
    </span>
  )
}

/** Maps a job or task status onto a tone, in one place, so the same status
 *  never reads green on one screen and grey on another. */
export function toneFor(status: string): 'neutral' | 'good' | 'warn' | 'bad' | 'brand' {
  switch (status) {
    case 'done': case 'review': case 'approved': case 'pass': return 'good'
    case 'failed': case 'fail': case 'aborted': case 'cancelled': case 'invalid': return 'bad'
    case 'qc_failed': return 'warn'
    case 'running': case 'generating': case 'queued': case 'unresolved': return 'warn'
    default: return 'neutral'
  }
}

export function StatusBadge({ status }: { status: string }) {
  const live = status === 'running' || status === 'generating' || status === 'queued'
  // `review` is the backend's terminal pipeline state. There is no review
  // action in the product, so exposing the persistence term makes a finished
  // video sound as though someone still needs to unblock it.
  const label = status === 'review' ? 'Ready' : status
  return <Badge tone={toneFor(status)} dot={live}>{label}</Badge>
}

// --- loading and empty ------------------------------------------------------

/** A destructive action that takes two clicks.
 *
 *  Inline rather than a modal dialog: a modal for every small deletion trains
 *  people to dismiss modals, which is worse than no confirmation at all. This
 *  puts the consequence next to the button and requires a deliberate second
 *  press, and reverts on its own so a stray first click does not leave the row
 *  armed.
 */
export function ConfirmButton({
  onConfirm, children, confirmLabel = 'Confirm', title, disabled, size = 'sm', className,
}: {
  onConfirm: () => void
  children: React.ReactNode
  confirmLabel?: string
  title?: string
  disabled?: boolean
  size?: 'sm' | 'md' | 'icon'
  className?: string
}) {
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    if (!armed) return
    const t = setTimeout(() => setArmed(false), 4000)
    return () => clearTimeout(t)
  }, [armed])

  if (!armed) {
    return (
      <Button variant="ghost" size={size} title={title} disabled={disabled}
        className={className} onClick={() => setArmed(true)}>
        {children}
      </Button>
    )
  }
  return (
    <span className={cn('flex items-center gap-1.5', className)}>
      <Button variant="danger" size="sm" disabled={disabled}
        onClick={() => { setArmed(false); onConfirm() }}>
        {confirmLabel}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setArmed(false)}>Cancel</Button>
    </span>
  )
}

/** A numbered step in a multi-part form.
 *
 *  Used where a page has an ORDER that is not otherwise visible. Cards alone
 *  say "here are three things"; numbers say "this one, then this one", which
 *  is the difference between a form you read and a form you follow. */
export function Step({ n, title, hint, children, className }: {
  n: number
  title: string
  hint?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('space-y-3', className)}>
      <StepHeader n={n} title={title} hint={hint} />
      <div className="space-y-3 pl-[1.9rem]">{children}</div>
    </div>
  )
}

/** The numbered heading on its own.
 *
 *  For a step whose body is a whole card: indenting an entire card under a
 *  number buys nothing, and the number alone carries the ordering. */
export function StepHeader({ n, title, hint }: {
  n: number
  title: string
  hint?: React.ReactNode
}) {
  return (
    <div className="flex items-baseline gap-2.5">
      <span className="grid size-5 shrink-0 place-items-center rounded-full bg-surface-3 text-[11px] font-medium text-ink-dim">
        {n}
      </span>
      <div>
        <span className="text-sm font-medium">{title}</span>
        {hint && <p className="mt-0.5 text-xs leading-relaxed text-ink-faint">{hint}</p>}
      </div>
    </div>
  )
}

export type MenuAction = {
  label: string
  icon?: React.ComponentType<{ className?: string }>
  danger?: boolean
  disabled?: boolean
  /** When set, choosing this asks for confirmation with this label first.
   *  Destructive items get one, so the menu cannot delete on a single click
   *  landing on the wrong row. */
  confirmLabel?: string
  onSelect: () => void
}

/** A kebab menu.
 *
 *  Hand-rolled rather than pulled in: this needs a button, a panel, and
 *  dismissal on outside-click and Escape, which is less code than the
 *  dependency and matches the picker already in the app. */
export function Menu({ actions, label = 'More actions', className }: {
  actions: MenuAction[]
  label?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState<MenuAction | null>(null)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) { setOpen(false); setConfirming(null) }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setConfirming(null) }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const close = () => { setOpen(false); setConfirming(null) }

  return (
    <div ref={box} className={cn('relative', className)}>
      <Button
        variant="ghost" size="icon" aria-label={label} aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <MoreVertical />
      </Button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 min-w-44 overflow-hidden rounded-[--radius-card] border border-line bg-surface-1 p-1 shadow-[0_16px_48px_oklch(0_0_0/0.5)]">
          {confirming ? (
            <div className="space-y-1 p-1">
              <p className="px-1 text-xs leading-relaxed text-ink-dim">
                {confirming.confirmLabel}
              </p>
              <div className="flex gap-1.5">
                <Button
                  variant="danger" size="sm"
                  onClick={() => { const a = confirming; close(); a.onSelect() }}
                >
                  {confirming.label}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            actions.map(a => (
              <button
                key={a.label}
                disabled={a.disabled}
                onClick={() => {
                  if (a.confirmLabel) setConfirming(a)
                  else { close(); a.onSelect() }
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-[--radius-control] px-2.5 py-1.5',
                  'text-left text-[13px] transition-colors disabled:opacity-40',
                  a.danger ? 'text-bad hover:bg-bad/10' : 'text-ink hover:bg-surface-2',
                )}
              >
                {a.icon && <a.icon className="size-3.5 shrink-0" />}
                {a.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('shimmer rounded-[--radius-control]', className)} />
}

export function EmptyState({
  icon: Icon, title, body, action,
}: {
  icon?: React.ComponentType<{ className?: string }>
  title: string
  body?: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="panel flex flex-col items-center gap-3 px-6 py-14 text-center">
      {Icon && (
        <div className="grid size-11 place-items-center rounded-full bg-surface-2 text-ink-faint">
          <Icon className="size-5" />
        </div>
      )}
      <div className="space-y-1">
        <p className="font-medium text-ink">{title}</p>
        {body && <p className="mx-auto max-w-md text-sm leading-relaxed text-ink-faint">{body}</p>}
      </div>
      {action}
    </div>
  )
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null
  const msg = error instanceof Error ? error.message : String(error)
  return (
    <p className="rounded-[--radius-control] border border-bad/30 bg-bad/10 px-3 py-2 text-sm text-bad">
      {msg}
    </p>
  )
}

/** A short explanation that stays out of the way until wanted.
 *
 *  Most of the guidance in this tool is worth having and worth hiding: it
 *  explains why a threshold exists, which matters once and then becomes
 *  noise on every subsequent visit. */
export function Note({ children, tone = 'neutral' }: {
  children: React.ReactNode
  tone?: 'neutral' | 'warn' | 'brand'
}) {
  const tones = {
    neutral: 'border-line bg-surface-2/60 text-ink-dim',
    warn: 'border-warn/30 bg-warn/8 text-warn',
    brand: 'border-brand/30 bg-brand/8 text-ink-dim',
  }
  return (
    <div className={cn('rounded-[--radius-control] border px-3 py-2 text-xs leading-relaxed', tones[tone])}>
      {children}
    </div>
  )
}
